import { useState } from 'react';
import { useApp, useCan } from '../../app/AppContext.js';
import { useAsync } from '../lib/useAsync.js';
import { formatDateTime } from '../lib/format.js';
import {
  Badge,
  Button,
  Card,
  CardHead,
  EmptyState,
  ErrorBlock,
  Loading,
  PageHeader,
  Stat,
} from '../components/primitives.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { useToast } from '../components/Toast.js';
import { IconCheck, IconRefresh } from '../components/icons.js';
import type { DeadJob, QueueSummary } from '../../data/port.js';

interface FilaData {
  resumo: QueueSummary;
  mortos: DeadJob[];
}

export function Fila() {
  const { data } = useApp();
  const toast = useToast();
  const podeReprocessar = useCan('console-operador');
  const [reprocessando, setReprocessando] = useState<string | null>(null);

  const estado = useAsync<FilaData>(
    async () => {
      const [resumo, mortos] = await Promise.all([data.getQueue(), data.listDeadJobs()]);
      return { resumo, mortos };
    },
    [data],
  );

  const reprocessar = async (job: DeadJob) => {
    setReprocessando(job.end2end);
    try {
      const ok = await data.requeue(job.end2end);
      if (ok) {
        toast.success('Job recolocado na fila', job.end2end);
        estado.reload();
      } else {
        toast.error('Não foi possível reprocessar', job.end2end);
      }
    } finally {
      setReprocessando(null);
    }
  };

  const colunas: Column<DeadJob>[] = [
    { key: 'e2e', header: 'end2end / chave', cell: (j) => <span className="num td--strong">{j.end2end}</span> },
    { key: 'tipo', header: 'Tipo', cell: (j) => <Badge tone="neutral">{j.type}</Badge> },
    { key: 'tent', header: 'Tent.', num: true, cell: (j) => j.attempts },
    { key: 'erro', header: 'Último erro', cell: (j) => <span className="td--muted">{j.lastError}</span> },
    { key: 'quando', header: 'Quando', cell: (j) => <span className="num">{formatDateTime(j.failedAt)}</span> },
    ...(podeReprocessar
      ? [
          {
            key: 'acao',
            header: '',
            align: 'right' as const,
            cell: (j: DeadJob) => (
              <Button
                variant="primary"
                size="sm"
                loading={reprocessando === j.end2end}
                onClick={() => void reprocessar(j)}
              >
                <IconRefresh size={14} /> Reprocessar
              </Button>
            ),
          },
        ]
      : []),
  ];

  const resumo = estado.data?.resumo;
  const mortos = estado.data?.mortos ?? [];

  return (
    <>
      <PageHeader
        title="Fila de jobs"
        desc="Baixas e disparos em processamento, e o que caiu em dead-letter."
        actions={
          <Button variant="ghost" onClick={estado.reload}>
            <IconRefresh size={15} /> Atualizar
          </Button>
        }
      />

      {estado.loading && !estado.data && <Card><Loading /></Card>}
      {estado.error && <ErrorBlock message={estado.error} />}

      {resumo && (
        <div className="grid grid--stats">
          <Stat label="Na fila" value={resumo.queued} accent="info" />
          <Stat label="Processando" value={resumo.processing} accent="info" />
          <Stat label="Concluídos" value={resumo.done} accent="ok" />
          <Stat
            label="Dead-letter"
            value={resumo.dead}
            accent={resumo.dead > 0 ? 'danger' : 'ok'}
            foot={resumo.dead > 0 ? 'exige reprocesso' : 'nenhum job morto'}
          />
        </div>
      )}

      {estado.data && (
        <Card>
          <CardHead title="Dead-letter" sub="Jobs que falharam além das tentativas" />
          <div className="card__body--flush">
            {mortos.length === 0 ? (
              <EmptyState
                icon={<IconCheck size={40} />}
                title="Nenhum job em dead-letter"
                desc="Baixas e disparos estão sendo processados sem falhas persistentes."
              />
            ) : (
              <DataTable columns={colunas} rows={mortos} keyOf={(j) => j.end2end} rowTone={() => 'danger'} />
            )}
          </div>
        </Card>
      )}
    </>
  );
}
